import { el, clear } from '../core/dom.js';
import { getState } from '../core/state.js';
import { navigate } from '../router.js';
import { CATEGORY_TO_DOMAIN } from '../domain/default-methods.js';
import type { BehaviorDomain } from '../core/types.js';

interface ResourceEntry {
  region: string;
  name: string;
  contact: string;
  url?: string;
}

const CRISIS_RESOURCES: ResourceEntry[] = [
  { region: 'US/Canada', name: 'Suicide & Crisis Lifeline', contact: '988', url: 'https://988lifeline.org' },
  { region: 'US', name: 'SAMHSA Helpline (substance use)', contact: '1-800-662-4357', url: 'https://www.samhsa.gov/find-help/national-helpline' },
  { region: 'US', name: 'National Council on Problem Gambling', contact: '1-800-GAMBLER', url: 'https://www.ncpgambling.org' },
  { region: 'UK', name: 'Samaritans', contact: '116 123', url: 'https://www.samaritans.org' },
  { region: 'UK', name: 'Drinkline', contact: '0300 123 1110' },
  { region: 'UK', name: 'GamCare', contact: '0808 8020 133', url: 'https://www.gamcare.org.uk' },
  { region: 'International', name: 'Find A Helpline', contact: 'findahelpline.com', url: 'https://findahelpline.com' },
];

const BY_DOMAIN: Record<string, ResourceEntry[]> = {
  alcohol: [
    { region: 'US', name: 'SAMHSA Helpline', contact: '1-800-662-4357' },
    { region: 'UK', name: 'Drinkline', contact: '0300 123 1110' },
  ],
  nicotine: [
    { region: 'US', name: '1-800-QUIT-NOW (state quitlines)', contact: '1-800-784-8669' },
    { region: 'UK', name: 'NHS Smokefree', contact: '0300 123 1044' },
  ],
  gambling: [
    { region: 'US', name: 'National Council on Problem Gambling', contact: '1-800-GAMBLER' },
    { region: 'UK', name: 'GamCare', contact: '0808 8020 133' },
  ],
  food: [
    { region: 'US', name: 'NEDA Helpline (eating disorders)', contact: '1-800-931-2237' },
    { region: 'UK', name: 'Beat (eating disorders)', contact: '0808 801 0677' },
  ],
};

export function renderResources(parent: HTMLElement): void {
  clear(parent);
  const st = getState();
  const habit = st.activeHabit;
  const domain: BehaviorDomain | undefined = habit
    ? (habit.domain ?? CATEGORY_TO_DOMAIN[habit.category])
    : undefined;

  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Support resources'),
    el('p', { class: 'muted' },
      'Wean is a pacing tool, not a clinician. If reducing this habit feels unsafe — physically or emotionally — please reach out.'),
  ));

  if (domain && BY_DOMAIN[domain]) {
    parent.appendChild(buildList(
      `${labelForDomain(domain)} support`,
      BY_DOMAIN[domain]!,
    ));
  }

  parent.appendChild(buildList('Crisis & general support', CRISIS_RESOURCES));

  parent.appendChild(el('div', { class: 'card card-accent' },
    el('h2', {}, 'Discreet usage tips'),
    el('p', { class: 'muted-sm' },
      'Privacy mode (in Settings) replaces habit labels with neutral copy across Today and notifications. Backups are local files — store them somewhere private.'),
    el('a', { href: '#/settings', class: 'btn block' }, 'Open Settings'),
  ));

  parent.appendChild(el('button', {
    class: 'sheet-cancel',
    onclick: () => navigate('/today'),
  }, 'Back to Now'));
}

function buildList(title: string, entries: ResourceEntry[]): HTMLElement {
  const card = el('div', { class: 'card' }, el('h2', {}, title));
  for (const r of entries) {
    const row = el('div', { class: 'list-row' },
      el('div', {},
        el('div', { class: 'label' }, r.name),
        el('div', { class: 'sub' }, `${r.region} · ${r.contact}`),
      ),
      r.url
        ? el('a', { href: r.url, class: 'btn ghost small', attrs: { target: '_blank', rel: 'noopener noreferrer' } }, 'Open')
        : el('span', {}),
    );
    card.appendChild(row);
  }
  return card;
}

function labelForDomain(d: BehaviorDomain): string {
  switch (d) {
    case 'nicotine': return 'Nicotine';
    case 'alcohol': return 'Alcohol';
    case 'cannabis': return 'Cannabis';
    case 'caffeine': return 'Caffeine';
    case 'digital': return 'Digital';
    case 'gambling': return 'Gambling';
    case 'shopping': return 'Shopping';
    case 'food': return 'Food';
    case 'other': return 'Other';
  }
}
